import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Crown, Users, Globe, XOctagon } from 'lucide-react';

// Phase constants
export enum MintPhase {
  NONE = 0,
  GTD = 1,
  Whitelist = 2,
  Public = 3,
}

// Phase information for each phase
const phaseInfo = {
  [MintPhase.NONE]: {
    name: 'MINT CLOSED',
    description: 'Minting is currently disabled',
    color: '#ff0000',
    glowColor: 'rgba(255, 0, 0, 0.7)',
    icon: XOctagon,
    gradient: 'linear-gradient(to right, #ff0000, #cc0000)',
  },
  [MintPhase.GTD]: {
    name: 'GTD MINT',
    description: 'Purrtardio Guaranteed Mint Phase',
    color: '#FFD700',
    glowColor: 'rgba(255, 215, 0, 0.7)',
    icon: Crown,
    gradient: 'linear-gradient(to right, #FFD700, #FFA500)',
  },
  [MintPhase.Whitelist]: {
    name: 'PURRDOM LIST',
    description: 'Whitelist Mint Phase',
    color: '#9932CC',
    glowColor: 'rgba(153, 50, 204, 0.7)',
    icon: Users,
    gradient: 'linear-gradient(to right, #9932CC, #800080)',
  },
  [MintPhase.Public]: {
    name: 'PUBLIC MINT',
    description: 'Open to All',
    color: '#00BFFF',
    glowColor: 'rgba(0, 191, 255, 0.7)',
    icon: Globe,
    gradient: 'rainbow',
  },
};

interface MintPhaseIndicatorProps {
  currentPhase: MintPhase;
  gtdEligibleCount?: number;
}

export function MintPhaseIndicator({ currentPhase, gtdEligibleCount = 0 }: MintPhaseIndicatorProps) {
  const [rainbowHue, setRainbowHue] = useState(0);
  const phase = phaseInfo[currentPhase];
  
  // Rainbow effect for Public phase
  useEffect(() => {
    if (currentPhase !== MintPhase.Public) return;
    
    const rainbowInterval = setInterval(() => {
      setRainbowHue(prev => (prev + 1) % 360);
    }, 50);
    
    return () => clearInterval(rainbowInterval);
  }, [currentPhase]);
  
  // Get appropriate gradient based on phase
  const getGradient = () => {
    if (currentPhase === MintPhase.Public) {
      return `linear-gradient(to right, 
        hsl(${rainbowHue}, 100%, 50%), 
        hsl(${(rainbowHue + 60) % 360}, 100%, 50%), 
        hsl(${(rainbowHue + 120) % 360}, 100%, 50%))`;
    }
    return phase.gradient;
  };
  
  // Get appropriate color based on phase
  const getColor = () => {
    if (currentPhase === MintPhase.Public) {
      return `hsl(${rainbowHue}, 100%, 50%)`;
    }
    return phase.color;
  };
  
  // Subtle glow animation
  const glowVariants = {
    animate: {
      boxShadow: [
        `0 0 5px ${phase.glowColor}`,
        `0 0 10px ${phase.glowColor}`,
        `0 0 5px ${phase.glowColor}`,
      ],
      transition: {
        duration: 2,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };
  
  return (
    <motion.div 
      className="relative p-4 rounded-lg overflow-hidden backdrop-blur-sm mx-auto mb-8 max-w-md"
      initial={{ opacity: 0, y: -10 }}
      animate={{ 
        opacity: 1, 
        y: 0,
        boxShadow: [
          `0 0 5px ${phase.glowColor}`,
          `0 0 10px ${phase.glowColor}`,
          `0 0 5px ${phase.glowColor}`,
        ]
      }}
      transition={{ 
        duration: 0.4,
        boxShadow: {
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut"
        }
      }}
      style={{ 
        border: `2px solid ${getColor()}`,
        boxShadow: `0 0 8px ${phase.glowColor}`,
        background: 'rgba(0, 0, 0, 0.7)'
      }}
    >
      {/* Background gradient effect */}
      <motion.div 
        className="absolute inset-0 z-0 opacity-30"
        style={{ 
          background: getGradient(),
          filter: 'blur(10px)'
        }}
      />
      
      <div className="relative z-10 flex items-center justify-center">
        <motion.div 
          className="mr-3 p-2 rounded-full"
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.3 }}
          style={{ 
            background: `rgba(0, 0, 0, 0.5)`,
            border: `2px solid ${getColor()}`,
            boxShadow: `0 0 5px ${phase.glowColor}`
          }}
        >
          {phase.icon && <phase.icon size={30} style={{ color: getColor() }} />}
        </motion.div>
        
        <div className="text-left">
          <motion.h3 
            className="font-bold text-lg tracking-wider"
            style={{ color: getColor() }}
          >
            {phase.name}
          </motion.h3>
          
          <motion.p 
            className="text-sm opacity-80 mt-1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.8 }}
            transition={{ delay: 0.2 }}
          >
            {phase.description}
          </motion.p>
          
         
          {/* {currentPhase === MintPhase.GTD && gtdEligibleCount > 0 && (
            <motion.p
              className="text-xs mt-1 font-bold"
              style={{ color: getColor() }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              Only <span className="text-sm">{gtdEligibleCount}</span> eligible across the entire Purrdom
            </motion.p>
          )} */}
          
          {/* Pulsing status indicator */}
          <div className="flex items-center mt-2">
            <motion.div 
              className="w-2 h-2 rounded-full mr-2"
              animate={{ 
                scale: [1, 1.3, 1],
                opacity: [0.6, 1, 0.6]
              }}
              transition={{
                duration: 1.5, 
                repeat: Infinity,
                repeatType: "mirror"
              }}
              style={{ background: getColor() }}
            />
            {currentPhase !== MintPhase.NONE && <span className="text-xs">Active now</span>}
          </div>
        </div>
      </div>
    </motion.div>
  );
} 